import {
	getInvoicesIdDownloadPath,
	type InvoiceFilterDto,
	type InvoiceListResponseDto,
	InvoiceStatus,
	type MarketResponseDto,
} from "Infrastructure/Api/Api";
import { UserPermissions } from "Infrastructure/Api/permissions";
import type { UserPermissionValues } from "Infrastructure/Components/PermissionGuard";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { formatCurrency } from "Infrastructure/Utilities/currencyFormat";
import { useDateFormat } from "Infrastructure/Utilities/dateFormat";
import { defaultTableConfig } from "Infrastructure/Utilities/defaultTableConfig";
import { downloadFile } from "Infrastructure/Utilities/downloadFile";
import { hasPermission } from "Infrastructure/Utilities/Permissions/hasPermission";
import { useInvoiceList } from "Order/Modules/Invoice/Api/Query/useInvoiceListQuery";
import { OrderPaths } from "Order/Routes/Routing";
import { useTableFilter } from "Shared/Hooks/useTableFilter";
import { Download, Link as LinkIconMui } from "@mui/icons-material";
import {
	Box,
	Chip,
	type ChipProps,
	IconButton,
	Stack,
	Typography,
} from "@mui/material";
import {
	createMRTColumnHelper,
	type MRT_DensityState,
	type MRT_PaginationState,
	type MRT_Row,
	type MRT_Updater,
} from "material-react-table";
import { Link, useNavigate } from "react-router";

type TableAction = {
	action: () => void;
	icon: JSX.Element;
	permission: UserPermissionValues;
	ariaLabel?: string;
};

const invoiceStatusChipColor: Record<InvoiceStatus, ChipProps["color"]> = {
	[InvoiceStatus.Paid]: "success",
	[InvoiceStatus.Pending]: "default",
	[InvoiceStatus.Cancelled]: "warning",
};

const invoiceTableResource = Resources.base.invoice.table;
const invoiceSharedResource = Resources.base.invoice.shared;
const orderTableResource = Resources.base.order.editForm;

export type InvoiceTableRow = InvoiceListResponseDto & {
	customer: {
		name: string;
		street: string;
		buildingNumber: string;
		postCode: string;
		city: string;
	};
	supplier: {
		name: string;
		street: string;
		buildingNumber: string;
		postCode: string;
		city: string;
	};
	actions?: TableAction[];
	market?: MarketResponseDto | null;
};

export const useInvoiceTable = (filter: InvoiceFilterDto) => {
	const { t } = useResource();
	const columnHelper = createMRTColumnHelper<InvoiceTableRow>();
	const { formatDate } = useDateFormat();
	const navigate = useNavigate();

	const { pageIndex, pageLimit, setSearchParams } = useTableFilter();

	const { data, isLoading } = useInvoiceList({
		...filter,
		pageIndex,
		pageLimit,
	});

	const handlePaginationChange = (
		updater: MRT_Updater<MRT_PaginationState>,
	) => {
		const currentState: MRT_PaginationState = {
			pageIndex,
			pageSize: pageLimit,
		};
		const newPagination =
			typeof updater === "function" ? updater(currentState) : updater;
		setSearchParams({
			pageIndex: newPagination.pageIndex,
			pageLimit: newPagination.pageSize,
		});
	};

	const tableData = (data?.data ?? []).map(
		(invoice: InvoiceListResponseDto) => ({
			...invoice,
			customer: {
				name: invoice.customerName,
				street: invoice.customerStreet,
				buildingNumber: invoice.customerBuildingNumber,
				postCode: invoice.customerPostCode,
				city: invoice.customerCity,
			},
			supplier: {
				name: invoice.supplierName,
				street: invoice.supplierStreet,
				buildingNumber: invoice.supplierBuildingNumber,
				postCode: invoice.supplierPostCode,
				city: invoice.supplierCity,
			},
			actions: [
				{
					action: () => downloadFile(getInvoicesIdDownloadPath(invoice.id)),
					icon: <Download />,
					permission: UserPermissions.InvoiceItemModule.view,
					ariaLabel: "Download invoice",
				},
			],
		}),
	);

	const columns = [
		columnHelper.accessor((row) => row.invoiceNumber, {
			id: "invoiceNumber",
			header: t(invoiceTableResource.invoiceNumber),
			enableColumnActions: false,
			maxSize: 150,
			grow: 0,
			Cell: ({ row }: { row: MRT_Row<InvoiceTableRow> }) => (
				<Typography variant="tableCell">
					{row.original.invoiceNumber}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.order?.id, {
			id: "orderId",
			header: t(invoiceTableResource.order),
			enableColumnActions: false,
			maxSize: 60,
			grow: 0,
			Cell: ({ row }) => (
				<Stack
					direction="row"
					columnGap={1}
					alignItems="center"
					minHeight="40px"
				>
					<Typography variant="tableCell">
						{row.original.order?.id ?? "-"}
					</Typography>
					{row.original.order?.id && (
						<Box
							sx={{ transition: "all 0.2s ease", ":hover": { opacity: 0.7 } }}
						>
							<Link to={OrderPaths.Invoice.path(row.original.order.id)}>
								<Typography variant="tableCell" color="secondary">
									<LinkIconMui fontSize="small" />
								</Typography>
							</Link>
						</Box>
					)}
				</Stack>
			),
		}),
		columnHelper.accessor((row) => row.variableSymbol, {
			id: "variableSymbol",
			header: t(invoiceTableResource.variableSymbol),
			enableColumnActions: false,
			maxSize: 150,
			grow: 0,
			Cell: ({ row }: { row: MRT_Row<InvoiceTableRow> }) => (
				<Typography variant="tableCell">
					{row.original.variableSymbol}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.status, {
			id: "status",
			header: t(invoiceTableResource.status),
			enableColumnActions: false,
			maxSize: 60,
			grow: 0,
			Cell: ({ row }: { row: MRT_Row<InvoiceTableRow> }) => {
				const displayStatus =
					row.original.status === InvoiceStatus.Pending
						? "Unpaid"
						: row.original.status;
				return (
					<Chip
						size="small"
						variant="outlined"
						label={displayStatus}
						color={invoiceStatusChipColor[row.original.status]}
					/>
				);
			},
		}),
		columnHelper.accessor((row) => row.totalAmountWithoutVat, {
			id: "totalAmountWithoutVat",
			header: t(invoiceTableResource.totalAmountWithoutVat),
			enableColumnActions: false,
			maxSize: 150,
			grow: 0,
			Cell: ({ row }: { row: MRT_Row<InvoiceTableRow> }) => (
				<Typography variant="tableCell">
					{formatCurrency(
						row.original.market?.country,
						row.original.market?.currency,
						row.original.totalAmountWithoutVat,
					)}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.totalVatAmount, {
			id: "totalVatAmount",
			header: t(invoiceTableResource.totalVatAmount),
			enableColumnActions: false,
			maxSize: 150,
			grow: 0,
			Cell: ({ row }: { row: MRT_Row<InvoiceTableRow> }) => (
				<Typography variant="tableCell">
					{formatCurrency(
						row.original.market?.country,
						row.original.market?.currency,
						row.original.totalVatAmount,
					)}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.totalAmountWithVat, {
			id: "totalAmountWithVat",
			header: t(invoiceTableResource.totalAmountWithVat),
			enableColumnActions: false,
			maxSize: 150,
			grow: 0,
			Cell: ({ row }: { row: MRT_Row<InvoiceTableRow> }) => (
				<Typography variant="tableCell">
					{formatCurrency(
						row.original.market?.country,
						row.original.market?.currency,
						row.original.totalAmountWithVat,
					)}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.totalDeposit, {
			id: "totalDeposit",
			header: t(invoiceTableResource.totalDeposit),
			enableColumnActions: false,
			maxSize: 150,
			grow: 0,
			Cell: ({ row }: { row: MRT_Row<InvoiceTableRow> }) => (
				<Typography variant="tableCell">
					{formatCurrency(
						row.original.market?.country,
						row.original.market?.currency,
						row.original.totalDeposit,
					)}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.bankName, {
			id: "bankName",
			header: t(invoiceTableResource.bankName),
			enableColumnActions: false,
			maxSize: 150,
			grow: 0,
			Cell: ({ row }: { row: MRT_Row<InvoiceTableRow> }) => (
				<Typography variant="tableCell">{row.original.bankName}</Typography>
			),
		}),
		columnHelper.accessor((row) => row.bankAccount, {
			id: "bankAccount",
			header: t(invoiceTableResource.bankAccount),
			enableColumnActions: false,
			maxSize: 150,
			grow: 0,
			Cell: ({ row }: { row: MRT_Row<InvoiceTableRow> }) => (
				<Typography variant="tableCell">{row.original.bankAccount}</Typography>
			),
		}),
		columnHelper.accessor((row) => row.iban, {
			id: "iban",
			header: t(invoiceTableResource.iban),
			enableColumnActions: false,
			maxSize: 150,
			grow: 0,
			Cell: ({ row }: { row: MRT_Row<InvoiceTableRow> }) => (
				<Typography variant="tableCell">{row.original.iban}</Typography>
			),
		}),
		columnHelper.accessor((row) => row.paymentMethod, {
			id: "paymentMethod",
			header: t(invoiceTableResource.paymentMethod),
			enableColumnActions: false,
			maxSize: 60,
			grow: 0,
			Cell: ({ row }: { row: MRT_Row<InvoiceTableRow> }) => (
				<Typography variant="tableCell">
					{row.original.paymentMethod}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.dateIssued, {
			id: "dateIssued",
			header: t(invoiceTableResource.dateIssued),
			enableColumnActions: false,
			maxSize: 150,
			grow: 0,
			Cell: ({ row }: { row: MRT_Row<InvoiceTableRow> }) => (
				<Typography variant="tableCell">
					{formatDate(row.original.dateIssued)}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.order?.dateDone, {
			id: "dateDone",
			header: t(orderTableResource.dateDone),
			enableColumnActions: false,
			maxSize: 150,
			grow: 0,
			Cell: ({ row }: { row: MRT_Row<InvoiceTableRow> }) => (
				<Typography variant="tableCell">
					{row.original.order?.dateDone
						? formatDate(row.original.order.dateDone)
						: "-"}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.dateDue, {
			id: "dateDue",
			header: t(invoiceTableResource.dateDue),
			enableColumnActions: false,
			maxSize: 150,
			grow: 0,
			Cell: ({ row }: { row: MRT_Row<InvoiceTableRow> }) => (
				<Typography variant="tableCell">
					{formatDate(row.original.dateDue)}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.datePaid, {
			id: "datePaid",
			header: invoiceTableResource.datePaid
				? t(invoiceTableResource.datePaid)
				: "",
			enableColumnActions: false,
			maxSize: 150,
			grow: 0,
			Cell: ({ row }: { row: MRT_Row<InvoiceTableRow> }) => (
				<Typography variant="tableCell">
					{row.original.datePaid ? formatDate(row.original.datePaid) : "-"}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.customerName, {
			id: "customer",
			header: t(invoiceTableResource.customer),
			maxSize: 150,
			grow: 0,
			sortingFn: (rowA, rowB) => {
				const nameA = rowA.original.customerName || "";
				const nameB = rowB.original.customerName || "";
				return nameA.localeCompare(nameB);
			},
			Cell: ({ row }: { row: MRT_Row<InvoiceTableRow> }) => (
				<Stack direction="column" flexWrap="wrap">
					<Typography variant="tableCell" color="primary">
						{row.original.customerName}
					</Typography>
					{(row.original.customerStreet ||
						row.original.customerBuildingNumber) && (
						<Typography variant="tableCell">
							{`${row.original.customerStreet ?? ""} ${row.original.customerBuildingNumber ?? ""}`}
						</Typography>
					)}
					{row.original.customerCity && (
						<Typography variant="tableCell">
							{row.original.customerCity ?? ""}
						</Typography>
					)}
				</Stack>
			),
		}),
		columnHelper.accessor((row) => row.customerBusinessId, {
			id: "customerBusinessId",
			header: t(invoiceTableResource.customerBusinessId),
			enableColumnActions: false,
			maxSize: 60,
			grow: 0,
			Cell: ({ row }: { row: MRT_Row<InvoiceTableRow> }) => (
				<Typography variant="tableCell">
					{row.original.customerBusinessId}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.customerVatId, {
			id: "customerVatId",
			header: t(invoiceTableResource.customerVatId),
			enableColumnActions: false,
			maxSize: 60,
			grow: 0,
			Cell: ({ row }: { row: MRT_Row<InvoiceTableRow> }) => (
				<Typography variant="tableCell">
					{row.original.customerVatId}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.customerVatLiable, {
			id: "customerVatLiable",
			header: t(invoiceTableResource.customerVatLiable),
			enableColumnActions: false,
			maxSize: 150,
			grow: 0,
			Cell: ({ row }: { row: MRT_Row<InvoiceTableRow> }) => (
				<Typography variant="tableCell">
					{row.original.customerVatLiable
						? t(invoiceSharedResource.isVatLiable)
						: t(invoiceSharedResource.isNotVatLiable)}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.supplierName, {
			id: "supplier",
			header: t(invoiceTableResource.supplier),
			maxSize: 150,
			grow: 0,
			sortingFn: (rowA, rowB) => {
				const nameA = rowA.original.supplierName || "";
				const nameB = rowB.original.supplierName || "";
				return nameA.localeCompare(nameB);
			},
			Cell: ({ row }: { row: MRT_Row<InvoiceTableRow> }) => (
				<Stack direction="column" flexWrap="wrap">
					<Typography variant="tableCell" color="primary">
						{row.original.supplierName}
					</Typography>
					{(row.original.supplierStreet ||
						row.original.supplierBuildingNumber) && (
						<Typography variant="tableCell">
							{`${row.original.supplierStreet ?? ""} ${row.original.supplierBuildingNumber ?? ""}`}
						</Typography>
					)}
					{row.original.supplierCity && (
						<Typography variant="tableCell">
							{row.original.supplierCity ?? ""}
						</Typography>
					)}
				</Stack>
			),
		}),
		columnHelper.accessor((row) => row.supplierBusinessId, {
			id: "supplierBusinessId",
			header: t(invoiceTableResource.supplierBusinessId),
			enableColumnActions: false,
			maxSize: 60,
			grow: 0,
			Cell: ({ row }: { row: MRT_Row<InvoiceTableRow> }) => (
				<Typography variant="tableCell">
					{row.original.supplierBusinessId}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.supplierVatId, {
			id: "supplierVatId",
			header: t(invoiceTableResource.supplierVatId),
			enableColumnActions: false,
			maxSize: 150,
			grow: 0,
			Cell: ({ row }: { row: MRT_Row<InvoiceTableRow> }) => (
				<Typography variant="tableCell">
					{row.original.supplierVatId}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.supplierVatLiable, {
			id: "supplierVatLiable",
			header: t(invoiceTableResource.supplierVatLiable),
			enableColumnActions: false,
			maxSize: 150,
			grow: 0,
			Cell: ({ row }: { row: MRT_Row<InvoiceTableRow> }) => (
				<Typography variant="tableCell">
					{row.original.supplierVatLiable
						? t(invoiceSharedResource.isVatLiable)
						: t(invoiceSharedResource.isNotVatLiable)}
				</Typography>
			),
		}),
		{
			id: "actions",
			header: t(invoiceSharedResource.actions),
			enableColumnActions: false,
			maxSize: 60,
			grow: 0,
			Cell: ({ row }: { row: MRT_Row<InvoiceTableRow> }) => (
				<Stack direction="row" columnGap={1}>
					{row.original.actions?.map(
						(action, index) =>
							hasPermission(action.permission) && (
								<IconButton
									key={index}
									onClick={action.action}
									aria-label={action.ariaLabel}
									size="small"
									sx={{
										background: "transparent",
										transition: "all 0.15s ease",
										"&:hover": { background: "transparent", opacity: 0.5 },
									}}
								>
									{action.icon}
								</IconButton>
							),
					)}
				</Stack>
			),
		},
	];

	const tableOptions = {
		...defaultTableConfig,
		columns,
		data: tableData,
		state: {
			showSkeletons: isLoading,
			isLoading,
			pagination: {
				pageSize: pageLimit,
				pageIndex: pageIndex,
			},
		},
		initialState: {
			density: "compact" as MRT_DensityState,
			columnVisibility: {
				customerBusinessInfo: false,
				totalAmount: false,
				totalVat: false,
				totalDeposit: false,
				variableSymbol: false,
				bankName: false,
				bankAccount: false,
				iban: false,
				paymentMethod: false,
				dateDue: false,
				datePaid: false,
				customerVatLiable: false,
				customerVatId: false,
				customerBusinessId: false,
				supplierVatId: false,
				supplierVatLiable: false,
				supplierBusinessId: false,
			},
		},
		enableColumnActions: false,
		muiPaginationProps: {
			showRowsPerPage: false,
			showFirstButton: false,
			showLastButton: false,
		},
		manualPagination: true,
		onPaginationChange: handlePaginationChange,
		rowCount: data?.metaData?.count ?? 0,
		muiTableBodyRowProps: ({ row }) => ({
			onDoubleClick: () => {
				if (row.original?.order?.id) {
					const orderID = row.original.order.id;
					navigate(OrderPaths.Invoice.path(orderID));
				}
			},
		}),
	};

	return tableOptions;
};
